// Cliente da Tela Inicial (Dashboard "Início") — épico ini.
// Consome GET /api/ia/dashboard/* (mod-ia). Auth: JWT em localStorage `pactua_token`.

import { tentarRefreshToken, temRefreshToken } from './token-refresh';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE ?? 'http://localhost:8000';
const DASH_BASE = `${API_BASE}/api/ia/dashboard`;

function authHeaders(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const token = localStorage.getItem('pactua_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function get<T>(path: string, jaTentouRefresh = false): Promise<T> {
  const res = await fetch(`${DASH_BASE}${path}`, { headers: authHeaders() });
  // Self-heal: access token expirado → renova e repete 1x (helper compartilhado).
  if (res.status === 401 && !jaTentouRefresh && temRefreshToken()) {
    const ok = await tentarRefreshToken();
    if (ok) return get<T>(path, true);
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ===== Tipos =====
export interface Metricas {
  processos_ativos: { valor: number; trend_semana: number };
  tarefas_pendentes: { valor: number; atribuidas_voce: number };
  documentos_gerados: { valor: number; ultimos_30_dias: number };
  taxa_aprovacao_ia: { valor_pct: number | null; delta_mes_pct: number | null };
}

export type DashDocStatus =
  | 'pendente' | 'gerando' | 'rascunho' | 'em_revisao' | 'aprovado' | 'rejeitado';

export interface ProcessoDoc {
  key: string;
  status: DashDocStatus;
}

export interface ProcessoParticipante {
  id: string | null;
  nome: string;
}

export interface ProcessoAtivo {
  id: string;
  codigo: string | null;
  objeto: string;
  modalidade: string | null;
  valor_estimado: number | null;
  estado: string;
  estado_label: string;
  progresso_pct: number;
  docs: ProcessoDoc[];
  participantes: ProcessoParticipante[];
  alertas: number;
  atualizado_em?: string | null;
}

export interface TarefaPendente {
  id: string;
  tipo: 'warning' | 'info' | 'success' | 'ai' | 'error';
  icone: string;
  titulo: string;
  processo: string;
  processo_codigo: string | null;
  processo_id: string | null;
  origem: string;
  quando: string;
  created_at: string | null;
}

export interface AtividadeItem {
  id: string;
  tipo: 'success' | 'ai' | 'error' | 'info' | 'warning' | 'default';
  texto: string;
  autor: string | null;
  processo_codigo: string | null;
  created_at: string | null;
  hora: string;
  grupo_dia: string;
}

export interface CopilotAcao {
  rota: string;
  codigo: string;
  processo_id: string | null;
}

export interface CopilotSugestao {
  tipo: 'precedente' | 'proximo_passo';
  titulo: string;
  texto: string;
  similaridade_pct?: number;
  processo_atual?: string;
  precedente_codigo?: string;
  precedente_id?: string;
  acao: CopilotAcao;
}

// ===== Dashboard Executivo (BI) — GET /bi =====
// Contrato REAL do mod-ia (services/ia/app/dashboard/router.py::bi).
// Princípio No-Invention: só agrega o que o schema `ia` suporta.
export interface DashboardBiKpis {
  // --- Estoque (ponto-no-tempo): SEMPRE atuais, não janelados por período (AC6). ---
  total_processos: number;
  processos_ativos: number;
  processos_concluidos: number;
  modalidades_distintas: number;
  // --- Fluxo: janelados pelo período quando há um selecionado. ---
  documentos_gerados: number;
  /** null quando não há documentos decididos (aprovado/rejeitado) ainda. */
  taxa_aprovacao_ia_pct: number | null;
  /** Processos criados no período; null quando nenhum período é pedido. */
  processos_criados: number | null;
}

export interface DashboardBiThroughput {
  mes: string;        // 'YYYY-MM'
  label: string;      // ex.: 'Mai/26'
  processos: number;
  documentos: number;
}

export interface DashboardBiPipeline {
  estado: string;
  label: string;
  count: number;
  cor: string;        // var(--...) vindo do backend
}

export interface DashboardBiModalidade {
  modalidade: string; // já é o rótulo legível (ex.: 'Pregão Eletrônico')
  count: number;
}

export interface DashboardBiGuardrail {
  regra: string;
  severidade: string;
  total: number;
  resolvido: number;
}

export type DashboardBiPeriodo = '30d' | '90d' | '7m' | '12m';

export interface DashboardBi {
  kpis: DashboardBiKpis;
  throughput: DashboardBiThroughput[];
  pipeline: DashboardBiPipeline[];
  modalidades: DashboardBiModalidade[];
  guardrails: DashboardBiGuardrail[];
  /** Sempre [] por design — lead-time/SLA dependem de definição de negócio. */
  lead_time: unknown[];
  /** Range efetivamente aplicado pelo backend (null = agregado/sem período). */
  periodo: DashboardBiPeriodo | null;
}

// fup-ui-1.4: `periodo` opcional. Sem ele = agregado (compat Tela Inicial);
// com ele, o backend recorta KPIs de fluxo + throughput pelo range.
export const buscarDashboardBi = (periodo?: DashboardBiPeriodo) =>
  get<DashboardBi>(periodo ? `/bi?periodo=${periodo}` : '/bi');

// ===== Comparação com precedente (ui-1.4-2) — GET /comparacao =====
export type CompMatch = 'igual' | 'reusar' | 'ajustar' | 'parcial';

export interface ComparacaoMeta {
  id: string;
  codigo: string | null;
  objeto: string;
  modalidade: string | null;
  valor_estimado: number | null;
  status: string;
  status_label: string;
}

export interface ComparacaoCampo {
  campo: string;
  atual: string;
  atual_vazio: boolean;
  precedente: string;
  match: CompMatch;
  similaridade_pct: number | null;
}

export interface ComparacaoSecao {
  documento: string;
  documento_label: string;
  similaridade_pct: number | null;
  campos: ComparacaoCampo[];
}

export interface ComparacaoLicao {
  tipo: string;
  texto: string;
  quando: string;
}

export interface Comparacao {
  atual: ComparacaoMeta;
  precedente: ComparacaoMeta;
  similaridade_pct: number | null;
  resumo: { reusaveis: number; total_campos: number };
  secoes: ComparacaoSecao[];
  licoes: ComparacaoLicao[];
  embeddings_ok: boolean;
}

export const buscarComparacaoPrecedente = (atual: string, precedente: string) =>
  get<Comparacao>(
    `/comparacao?atual=${encodeURIComponent(atual)}&precedente=${encodeURIComponent(precedente)}`,
  );

// ===== Fetchers =====
export const buscarMetricasDashboard = () => get<Metricas>('/metricas');
export const buscarProcessosAtivos = (limit = 6) =>
  get<{ processos: ProcessoAtivo[] }>(`/processos-ativos?limit=${limit}`);
// ad-E2-0: o grid de Processos NÃO consome mais daqui — ele lê a fila única em
// `lib/fila-api.ts`. O endpoint `/processos-ativos` continua intocado e serve ao
// bloco do Início; o helper que trazia a lista inteira saiu porque um segundo
// caminho para a mesma tela é como duas filas nascem sem ninguém decidir isso.
export const buscarTarefasPendentes = (limit = 5) =>
  get<{ total: number; tarefas: TarefaPendente[] }>(`/tarefas?limit=${limit}`);
export const buscarAtividadesRecentes = (limit = 8) =>
  get<{ atividades: AtividadeItem[] }>(`/atividades?limit=${limit}`);
export const buscarCopilotSugestoes = () =>
  get<{ sugestoes: CopilotSugestao[] }>('/copilot-sugestoes');

// Mapeia status do backend → DocStatus do componente DocCircle.
export function toDocStatus(s: string): 'pendente' | 'gerando' | 'rascunho' | 'revisao' | 'aprovado' | 'rejeitado' {
  if (s === 'em_revisao') return 'revisao';
  if (['pendente', 'gerando', 'rascunho', 'aprovado', 'rejeitado'].includes(s)) {
    return s as 'pendente' | 'gerando' | 'rascunho' | 'aprovado' | 'rejeitado';
  }
  return 'pendente';
}

// Formata valor (reais) — espelha fmtMoney do protótipo.
export function fmtMoney(v: number | null | undefined): string {
  if (v == null) return '—';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}
